'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { type CSSProperties, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useMyLook, useUserActivitiesQuery } from '@/lib/api/auth/hooks';
import { useKycStatus } from '@/lib/api/kyc/hooks';
import { BalancePopover } from '@/lib/components/BalancePopover';
import { AppDownloadPopover } from '@/lib/components/Header/AppDownloadPopover';
import { HeaderNav } from '@/lib/components/Header/HeaderNav';
import { NavMobileDialog } from '@/lib/components/Header/NavMobileDialog';
import { NotificationsBellButton } from '@/lib/components/NotificationsBellButton';
import { DailyCardHeaderButton } from '@/lib/components/RewardsHub/DailyCardHeaderButton';
import { SearchPopoverV2 } from '@/lib/components/SearchPopoverV2';
import { useGeolocation } from '@/lib/hooks/useGeolocation';
import { Link } from '@/lib/routing';
import { AnimatedLogo, WORDMARK_ON_DARK } from '@/lib/ui-kit/animated-logo';
import { Icon } from '@/lib/ui-kit/icons';
import type { ColorIconName } from '@/lib/ui-kit/icons/color';
import { preloadAnimation } from '@/lib/ui-kit/lottie-illustration';
import { Popover, PopoverContent, PopoverTrigger } from '@/lib/ui-kit/popover';
import { Skeleton } from '@/lib/ui-kit/skeleton';
import { cn } from '@/lib/ui-kit/utils';
import { AccountMenu } from '@/lib/ui-kit-v2/account-menu';
import { BurgerIcon } from '@/lib/ui-kit-v2/burger-icon';
import { Button } from '@/lib/ui-kit-v2/button';
import { UserAvatar } from '@/lib/ui-kit-v2/user-avatar';
import { UserMenu } from '@/lib/ui-kit-v2/user-menu';
import { headerFlagFor, rememberHeaderFlag } from '@/lib/utils/headerFlag';

type Theme = 'light' | 'dark';

interface HeaderV2User {
  id?: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
}

interface HeaderV2LanguageOption {
  code: string;
  display: string;
  label: string;
  flag: ColorIconName;
}

interface HeaderV2MenuProps {
  theme?: Theme;
  onThemeChange?: (theme: Theme) => void;
  language?: string;
  flag?: ReactNode;
  onLanguageClick?: () => void;
  languageOptions?: HeaderV2LanguageOption[];
  currentLanguageCode?: string;
  onLanguageChange?: (code: string) => void;
  onProfileClick?: () => void;
  onSettingsClick?: () => void;
  onHelpCenterClick?: () => void;
  onWalletClick?: () => void;
  onRewardsHubClick?: () => void;
  onPromocodesClick?: () => void;
  onLeaderboardClick?: () => void;
  onNewsClick?: () => void;
  onApiClick?: () => void;
  onAddEmail?: () => void;
  onLogout?: () => void;
}

interface HeaderV2Props extends HeaderV2MenuProps {
  user?: HeaderV2User | null;
  isAuthLoading?: boolean;
  /** Wordmark fill; the container picks it from the theme. */
  wordmarkFill?: string;
  onLogin?: () => void;
  onSignup?: () => void;
  /** Cross-zone hrefs for Log In / Sign Up — when set, the buttons render as <a>. */
  loginHref?: string;
  signupHref?: string;
  onDeposit?: () => void;
  onConvert?: () => void;
  /** Currency of the wallet popover's trading balance; defaults to USDC. */
  balanceCurrency?: string;
  onAvatarClick?: () => void;
  onMenuClick?: () => void;
  className?: string;
}

export function HeaderV2({
  user,
  isAuthLoading = false,
  onLogin,
  onSignup,
  onDeposit,
  onConvert,
  balanceCurrency = 'USDC',
  onAvatarClick,
  onMenuClick,
  wordmarkFill = WORDMARK_ON_DARK,
  className,
  ...menuProps
}: HeaderV2Props) {
  const isAuthenticated = Boolean(user);

  const { data: geo, isFetched: geoSettled } = useGeolocation();
  // The last login's country is only the fallback for a request geolocation
  // could not place, so it is not read while geolocation still may.
  const { data: activities } = useUserActivitiesQuery({
    enabled: isAuthenticated && geoSettled && !geo?.country,
  });
  const headerFlag = headerFlagFor(geo?.country || activities?.[0]?.geoCountry);
  // What geolocation answered is what the next page load paints first.
  useEffect(() => {
    if (geo?.country) rememberHeaderFlag(geo.country);
  }, [geo?.country]);

  return (
    <header className={cn('w-full bg-transparent', className)}>
      {/* The same box in every zone, the full-width trade terminal included:
          a zone of its own would move the whole header on every tab switch
          between zones (40px at 1440, 268px at 1920; 13.10). */}
      {/* Figma 14625:42632 / 15126:96637 / 14973:1889: 24px sides at 768, 32 at 1200, 60 at 1440. */}
      <div className="mx-auto flex w-full max-w-[1464px] items-center gap-4 px-4 py-1 sm:px-6 md:px-8 lg:px-[60px]">
        {/* Logo */}
        {/* Presses like a button on tap — same scale/curve as ui-kit/button. */}
        <Link
          href="/"
          aria-label="Outpoll home"
          className="flex shrink-0 select-none items-center gap-1 transition-transform duration-150
            ease-out active:scale-[97%] motion-reduce:transition-none motion-reduce:active:scale-100"
        >
          {/* Static mark. The spin-on-navigation phase is wired up in
              ui-kit/animated-logo.tsx but deliberately not driven here. */}
          <AnimatedLogo wordmarkFill={wordmarkFill} width={117} />
          {headerFlag ? (
            <Image
              src={headerFlag.src}
              alt={headerFlag.name}
              width={24}
              height={16}
              fetchPriority="high"
              className="shrink-0 rounded-[3px]"
            />
          ) : null}
          {/* Until geolocation answers, the flag the last page settled on. */}
          {!headerFlag && !geoSettled ? <HeaderFlagSlot /> : null}
        </Link>

        {/* Nav from 1024 (below it the burger carries it): 32px after the logo,
            40 from 1440. What doesn't fit scrolls. */}
        <HeaderNav
          isAuthenticated={isAuthenticated}
          isAuthLoading={isAuthLoading}
          onDeposit={onDeposit}
          onConvert={onConvert}
          className="hidden min-w-0 flex-1 min-[1024px]:ml-4 min-[1024px]:block lg:ml-6"
        />

        {/* Search */}
        <div className="hidden w-[170px] shrink-0 min-[1024px]:block">
          <SearchPopoverV2 />
        </div>

        {/* Right-side actions */}
        <div className="ml-auto flex min-w-0 items-center sm:shrink-0 min-[1024px]:ml-0">
          {isAuthLoading ? (
            /* Auth state lives in localStorage, so SSR can't know it and both
               placeholders ship in the HTML — SESSION_EVIDENCE_SCRIPT stamps
               <html data-no-session> before paint and CSS picks one, which is
               how a visitor with no session gets the real buttons instead of a
               skeleton that waits for hydration. */
            <>
              <ActionsSkeleton className="[[data-no-session]_&]:hidden" />
              <GuestActions
                className="hidden [[data-no-session]_&]:flex"
                onLogin={onLogin}
                onSignup={onSignup}
                onMenuClick={onMenuClick}
                menuProps={menuProps}
              />
            </>
          ) : isAuthenticated ? (
            <AuthenticatedActions
              user={user as HeaderV2User}
              onDeposit={onDeposit}
              onConvert={onConvert}
              balanceCurrency={balanceCurrency}
              onAvatarClick={onAvatarClick}
              menuProps={menuProps}
            />
          ) : (
            <GuestActions
              onLogin={onLogin}
              onSignup={onSignup}
              onMenuClick={onMenuClick}
              menuProps={menuProps}
            />
          )}
        </div>
      </div>
    </header>
  );
}

const FLAG_SLOT_STYLE: CSSProperties = {
  backgroundImage: 'var(--header-flag)',
  backgroundSize: '100% 100%',
};

/**
 * The flag before geolocation answers. It ships in the HTML hidden, and shows
 * — in the image's own 24×16 box — when HEADER_FLAG_SCRIPT has stamped a flag
 * on `<html>` before paint. Once geolocation answers, the real image or
 * nothing takes its place, so a cross-zone page load no longer shifts the nav.
 */
function HeaderFlagSlot() {
  return (
    <span
      aria-hidden
      data-header-flag-slot
      className="hidden h-4 w-6 shrink-0 rounded-[3px] [[data-header-flag]_&]:block"
      style={FLAG_SLOT_STYLE}
    />
  );
}

function ActionsSkeleton({ className }: { className?: string }) {
  const t = useTranslations('nav');
  // Outer wrapper must mirror `AuthenticatedActions` / `GuestActions` so the
  // SSR→CSR swap from skeleton to real content doesn't trip a hydration error.
  return (
    // Slots mirror AuthenticatedActions (Deposit, wallet, bell, avatar)
    // so the swap doesn't shove the search box sideways.
    <div className={cn('flex min-w-0 items-center gap-4', className)}>
      <div className="flex items-center gap-4">
        {/* Invisible label sizes the slot like the real Deposit button in every locale. */}
        <Skeleton aria-hidden className="flex h-[30px] items-center rounded-[10px] px-3">
          <span className="invisible text-[12px] leading-[1.15]">{t('deposit')}</span>
        </Skeleton>
        {/* The wallet trigger: 40px icon and a 16px chevron. */}
        <Skeleton aria-hidden className="hidden h-10 w-14 rounded-[8px] min-[1024px]:block" />
      </div>
      <Skeleton aria-hidden className="size-10 rounded-full" />
      {/* The avatar; the burger takes its place from 768 to 1024. */}
      <Skeleton
        aria-hidden
        className="size-8 rounded-full sm:max-[1024px]:size-10 sm:max-[1024px]:rounded-[8px]"
      />
    </div>
  );
}

interface GuestActionsProps {
  onLogin?: () => void;
  onSignup?: () => void;
  onMenuClick?: () => void;
  menuProps: HeaderV2MenuProps;
  className?: string;
}

function GuestActions({ onLogin, onSignup, onMenuClick, menuProps, className }: GuestActionsProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const t = useTranslations('nav');
  return (
    <div className={cn('flex min-w-0 items-center gap-4 sm:gap-2', className)}>
      {/* Only ever shows for a guest with the rewards mocks on (localhost). */}
      <DailyCardHeaderButton />
      <Button
        appearance="ghost"
        size="S"
        onClick={onLogin}
        className="hidden min-w-0 max-w-[110px] !shrink min-[371px]:inline-flex sm:max-w-none"
      >
        <span className="block min-w-0 truncate">{t('login')}</span>
      </Button>
      <Button
        appearance="accent"
        size="S"
        onClick={onSignup}
        className="min-w-0 max-w-[120px] !shrink sm:max-w-none"
      >
        <span className="block min-w-0 truncate">{t('signup')}</span>
      </Button>

      {/* Download-app QR popover (from 1024; Figma 14973:1990 drops it at 768) */}
      <div className="hidden min-[1024px]:block">
        <AppDownloadPopover />
      </div>

      {/* Desktop: popover with UserMenu (≥1024px) */}
      <div className="hidden min-[1024px]:block">
        <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <PopoverTrigger asChild>
            <Button appearance="icon" size="M" aria-label={t('userMenu')} onClick={onMenuClick}>
              <BurgerIcon isOpen={isMenuOpen} />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={8}
            className="w-auto border-0 bg-transparent p-0 shadow-none"
          >
            <UserMenu
              theme={menuProps.theme}
              onThemeChange={menuProps.onThemeChange}
              language={menuProps.language}
              flag={menuProps.flag}
              onLanguageClick={menuProps.onLanguageClick}
              languageOptions={menuProps.languageOptions}
              currentLanguageCode={menuProps.currentLanguageCode}
              onLanguageChange={menuProps.onLanguageChange}
              onRewardsHubClick={menuProps.onRewardsHubClick}
              onLeaderboardClick={menuProps.onLeaderboardClick}
              onNewsClick={menuProps.onNewsClick}
              onApiClick={menuProps.onApiClick}
              onHelpCenterClick={menuProps.onHelpCenterClick}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Below 1024: full-screen dialog, which also carries the nav sections */}
      <div className="min-[1024px]:hidden">
        <NavMobileDialog isGuest>
          <Button appearance="icon" size="M" aria-label={t('userMenu')}>
            <Icon name="Burger2" size={24} />
          </Button>
        </NavMobileDialog>
      </div>
    </div>
  );
}

// Figma 14625:42692: 1px text/txt-04 (#34373e, no v2 token) ring in dark.
const HEADER_AVATAR_BORDER = 'border border-v2-border dark:border-[#34373e]';

interface AuthenticatedActionsProps {
  user: HeaderV2User;
  onDeposit?: () => void;
  onConvert?: () => void;
  balanceCurrency: string;
  onAvatarClick?: () => void;
  menuProps: HeaderV2MenuProps;
}

function AuthenticatedActions({
  user,
  onDeposit,
  onConvert,
  balanceCurrency,
  onAvatarClick,
  menuProps,
}: AuthenticatedActionsProps) {
  const t = useTranslations('nav');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const look = useMyLook();
  const { data: kycStatus } = useKycStatus();

  const dismissingMenuProps = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(menuProps).map(([key, value]) =>
          typeof value !== 'function'
            ? [key, value]
            : [
                key,
                (...args: unknown[]) => {
                  (value as (...a: unknown[]) => unknown)(...args);
                  setIsMenuOpen(false);
                },
              ],
        ),
      ) as HeaderV2MenuProps,
    [menuProps],
  );

  return (
    <div className="group/actions flex min-w-0 items-center gap-4">
      <DailyCardHeaderButton />
      {/* Below 400px the header cannot hold both: the daily card takes Deposit's
          place. The whole group goes (the wallet is hidden there anyway), or
          its empty box would still take a gap. */}
      <div className="flex items-center gap-4 max-[400px]:group-has-[[data-daily-card-header]]/actions:hidden">
        <Button appearance="accent" size="S" onClick={onDeposit}>
          {t('deposit')}
        </Button>

        <BalancePopover
          onDeposit={onDeposit}
          onConvert={onConvert}
          balanceCurrency={balanceCurrency}
        />
      </div>

      {/* Figma: 4px from the wallet, which shows from 1024. */}
      <div className="min-[1024px]:-ml-3">
        <NotificationsBellButton />
      </div>

      {/* Desktop: Popover (≥1024px) */}
      <div className="hidden min-[1024px]:block">
        <Popover
          open={isMenuOpen}
          onOpenChange={(open) => {
            // Warm the log-out illustration while the menu is open — it is one
            // click away and the mobile menu skips the dialog entirely.
            if (open) preloadAnimation('log-out');
            setIsMenuOpen(open);
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={onAvatarClick}
              data-rewards-flight-target
              aria-label={t('userMenu')}
              className="block shrink-0 cursor-pointer rounded-full outline-none transition-opacity hover:opacity-90"
            >
              <UserAvatar
                userId={user.id}
                name={user.name}
                src={user.avatarUrl}
                look={look}
                size={32}
                className={HEADER_AVATAR_BORDER}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={8}
            className="w-auto border-0 bg-transparent p-0 shadow-none"
          >
            <AccountMenu
              user={user}
              isVerified={kycStatus?.status === 'APPROVED'}
              {...dismissingMenuProps}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* 768–1024: a burger opens the full-screen dialog (Figma 14973:1889) */}
      <div className="hidden sm:max-[1024px]:block">
        <NavMobileDialog isGuest={false} onDeposit={onDeposit} onConvert={onConvert}>
          <Button appearance="icon" size="M" aria-label={t('userMenu')}>
            <Icon name="Burger2" size={24} />
          </Button>
        </NavMobileDialog>
      </div>

      {/* Phones: the avatar opens it */}
      <div className="sm:hidden">
        <NavMobileDialog isGuest={false} onDeposit={onDeposit} onConvert={onConvert}>
          <button
            type="button"
            data-rewards-flight-target
            aria-label={t('userMenu')}
            className="block shrink-0 cursor-pointer rounded-full outline-none transition-opacity hover:opacity-90"
          >
            <UserAvatar
              userId={user.id}
              name={user.name}
              src={user.avatarUrl}
              look={look}
              size={36}
              className={HEADER_AVATAR_BORDER}
            />
          </button>
        </NavMobileDialog>
      </div>
    </div>
  );
}

export type { HeaderV2Props, HeaderV2User };
